// AuthorByline — namngiven Person + Reviewer för E-E-A-T-signal.
// Renderar UI + injicerar Person-schema som JSON-LD. Använd både på
// /kunskap/$slug och /guider/$slug. Personerna kommer från
// src/content/editorial.ts — byt namn/foto/LinkedIn där, inte här.

import { Link } from "@tanstack/react-router";
import { person } from "@/lib/seo/schema";
import {
  EDITORIAL_PEOPLE,
  EDITORIAL_POLICY_PATH,
  authorId,
  authorPath,
  type EditorialPerson,
} from "@/content/editorial";

export type AuthorMeta = EditorialPerson;

export const AUTHORS: Record<string, AuthorMeta> = EDITORIAL_PEOPLE;


export function AuthorByline({
  authorKey = "default",
  reviewedBy,
  date,
  reviewedDate,
  compact = false,
}: {
  authorKey?: keyof typeof AUTHORS | string;
  reviewedBy?: string; // person key
  date?: string; // ISO
  reviewedDate?: string; // ISO — senast materiellt granskad/uppdaterad
  compact?: boolean;
}) {
  const a = AUTHORS[authorKey] ?? AUTHORS.default;
  const r = reviewedBy ? (AUTHORS[reviewedBy] ?? null) : null;

  // Samma @id och url som författarsidan — bylinen blir en verifierbar
  // referens till en person, inte ett löst namn.
  const authorSlug = (EDITORIAL_PEOPLE[authorKey as string] ? (authorKey as string) : "default");
  const personSchema = {
    ...person({
      name: a.name,
      jobTitle: a.role,
      path: authorPath(authorSlug),
      image: a.image,
      knowsAbout: a.knowsAbout,
      description: a.credentials,
      sameAs: [a.linkedin].filter(Boolean) as string[],
    }),
    "@id": authorId(authorSlug),
  };

  const fmt = (d: string) =>
    new Date(d).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const fmtReviewed =
    reviewedDate && reviewedDate.slice(0, 10) !== date?.slice(0, 10)
      ? fmt(reviewedDate)
      : null;

  const fmtDate = date
    ? new Date(date).toLocaleDateString("sv-SE", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <>
      <div
        className={
          compact
            ? "flex items-center gap-3 text-sm text-fg/70"
            : "flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-fg/70"
        }
        data-author={a.name}
      >
        {a.image ? (
          <img
            src={a.image}
            alt={a.name}
            width={36}
            height={36}
            loading="lazy"
            decoding="async"
            className="h-9 w-9 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-mint/15 text-base">
            {a.name.charAt(0)}
          </span>
        )}
        <div className="leading-tight">
          <div className="text-fg">
            Av{" "}
            <Link
              to={authorPath(authorSlug)}
              rel="author"
              className="font-medium underline-offset-2 hover:underline"
            >
              {a.name}
            </Link>
          </div>
          <div className="text-xs text-fg/60">{a.role}</div>
        </div>
        {r && (
          <div className="text-xs text-fg/60">
            Granskad av <span className="text-fg">{r.name}</span>, {r.role}
          </div>
        )}
        {fmtDate && (
          <time className="text-xs text-fg/60" dateTime={date}>
            Publicerad {fmtDate}
          </time>
        )}
        {fmtReviewed && (
          <time className="text-xs text-fg/60" dateTime={reviewedDate}>
            Senast granskad {fmtReviewed}
          </time>
        )}
        <Link
          to={EDITORIAL_POLICY_PATH}
          className="text-xs text-fg/60 underline-offset-2 hover:underline"
        >
          Så granskar vi
        </Link>
      </div>
      {!compact && a.credentials && (
        <p className="mt-2 text-xs text-fg/60">{a.credentials}</p>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
    </>
  );
}
